<script lang="ts" setup>
import { defineProps } from 'vue';
import CircularProgress from '@/components/CircularProgress.vue';
import PercentageBadgeWithLink from '@/components/Badge/PercentageBadgeWithLink.vue';
import { CampaignsStatistics } from '@/api/campaigns';
import { useRoute } from 'vue-router/composables';
import { usePanelStore } from '@/store/pinia/panel';
import ProspectsPanel from '@/components/Prospects/ProspectsPanel.vue';

const route = useRoute();
const panelStore = usePanelStore();

const props = defineProps<{
  campaign: CampaignsStatistics;
  index: number;
}>();

const emit = defineEmits(['open-panel']);

function openPanel(type: string) {
  if (!type) return;
  
  // Open panel with appropriate type
  panelStore.openModal(ProspectsPanel, { 
    type,
    campaignId: props.campaign._id
  });
};
</script>

<template>
  <div class="contents">
    <div class="grid-cell font-bold text-blue-900" v-tooltip="campaign.name">
      <span class="line-clamp-3 truncate text-wrap">{{ campaign.name }}</span>
    </div>
    <div class="grid-cell flex justify-center">
      {{ campaign.prospectsStatistics.totalProspects }}
    </div>
    <div class="grid-cell flex justify-center">
      {{ campaign.prospectsStatistics.preparedProspects }}
    </div>
    <div class="grid-cell flex justify-center">
      {{ campaign.prospectsStatistics.ongoingProspects }}
    </div>
    <div class="grid-cell flex justify-center">
      <CircularProgress :current="campaign.prospectsStatistics.finishedProspects"
        :total="campaign.prospectsStatistics.totalProspects" />
      <span class="ml-[5px]">{{ campaign.prospectsStatistics.finishedProspects }}/{{
        campaign.prospectsStatistics.totalProspects }}</span>
    </div>
    <div class="grid-cell flex justify-center">
      <PercentageBadgeWithLink :percentage="campaign.prospectsStatistics.rates?.contactRate" :has-link="!!campaign.prospectsStatistics.rates?.contactRate" class="contacted-badge"
      @click.native="openPanel('contacted')" />
    </div>
    <div class="grid-cell flex justify-center">
      <PercentageBadgeWithLink :percentage="campaign.prospectsStatistics.rates?.acceptanceRate" :has-link="!!campaign.prospectsStatistics.rates?.acceptanceRate" class="accepted-badge"
      @click.native="openPanel('accepted')" />
    </div>
    <div class="grid-cell flex justify-center">
      <PercentageBadgeWithLink :percentage="campaign.prospectsStatistics.rates?.responseRate" :has-link="!!campaign.prospectsStatistics.rates?.responseRate" class="answered-badge"
      @click.native="openPanel('responded')" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.grid-cell {
  @apply px-4 py-1 h-[3.5rem] flex items-center border-b border-gray-200;
}

.contacted-badge {
  @apply bg-blue-200 text-blue-500;
}

.accepted-badge {
  @apply bg-orange-100 text-orange-400;
}

.answered-badge {
  @apply bg-green-200 text-green-500;
}
</style>